import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, BookOpen, CircleCheck, CircleDot, CircleX, Layers, RotateCcw, Send, Target } from 'lucide-react';
import { Button } from '../ui';
import { Markdown } from '../ui/Markdown';
import { aiErrorInfo, aiService } from '../../services/api';
import { AIState, stateOfError } from './AIStates';

const ROUND = 5;
const VERDICT = {
  CERTA: { label: 'Certa', icon: CircleCheck, tone: 'ok' },
  PARCIAL: { label: 'Parcial', icon: CircleDot, tone: 'warn' },
  ERRADA: { label: 'Errada', icon: CircleX, tone: 'bad' },
};

/**
 * Revisão ativa como sessão (2b): 5 perguntas abertas, nota Certa / Parcial / Errada com XP,
 * o que faltou e "Guardar como flashcard".
 */
export function ActiveReview({ ctx, title, status, onBack, onSource, onUsage }) {
  const [items, setItems] = useState([]);
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [grade, setGrade] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const started = useRef(false);

  const next = async () => {
    setBusy('next');
    setError(null);
    setGrade(null);
    setAnswer('');
    setSaved(false);
    try {
      const q = await aiService.reviewNext({ ...ctx, asked: items.map(i => i.question) });
      setQuestion(q);
      onUsage?.();
    } catch (err) {
      setError(aiErrorInfo(err));
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    next();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!answer.trim() || !question) return;
    setBusy('grade');
    setError(null);
    try {
      const g = await aiService.reviewGrade({ ...ctx, question: question.question, answer });
      setGrade(g);
      setItems(list => [...list, { question: question.question, focus: question.focus, answer, ...g }]);
      onUsage?.();
    } catch (err) {
      setError(aiErrorInfo(err));
    } finally {
      setBusy(null);
    }
  };

  const saveCard = async () => {
    if (!question || !grade) return;
    const back = [grade.right, grade.missing].filter(Boolean).join(' ');
    try {
      await aiService.saveFlashcards({ ...ctx, source: 'active_review', cards: [{ front: question.question, back: back || answer }] });
      setSaved(true);
    } catch (err) {
      setError(aiErrorInfo(err));
    }
  };

  const restart = () => {
    setItems([]);
    setQuestion(null);
    setGrade(null);
    started.current = true;
    setTimeout(next, 0);
  };

  const done = items.length >= ROUND && grade;
  const n = Math.min(ROUND, items.length + (grade ? 0 : 1));
  const xp = items.reduce((s, i) => s + (i.xp || 0), 0);
  const last = items.length && !grade ? items[items.length - 1] : null;

  return (
    <div className="ai-review">
      <div className="ai-review__head">
        <button type="button" className="ss-iconbtn" onClick={onBack} aria-label="Voltar para a conversa"><ArrowLeft size={16} aria-hidden="true" /></button>
        <span className="ai-review__title"><strong>Me pergunte</strong><span>{title}</span></span>
        <span className="ai-review__count"><b>{n}</b> / {ROUND}</span>
      </div>
      <span className="ai-review__bar"><span style={{ width: `${(items.length / ROUND) * 100}%` }} /></span>

      <div className="ai-review__body">
        {last && <PrevResult item={last} n={items.length} />}

        {error ? (
          <AIState kind={stateOfError(error.code)} status={status} message={error.code === 'AI_DAILY_LIMIT' ? error.message : error.message}
                   onRetry={stateOfError(error.code) === 'ERROR' ? (grade || !question ? next : submit) : undefined} compact />
        ) : done ? (
          <div className="ai-review__done">
            <Target size={26} aria-hidden="true" />
            <strong>Rodada concluída</strong>
            <span>{items.filter(i => i.verdict === 'CERTA').length} certas · {items.filter(i => i.verdict === 'PARCIAL').length} parciais · +{xp} XP</span>
            <ul>
              {items.map((i, k) => {
                const v = VERDICT[i.verdict] || VERDICT.ERRADA;
                return <li key={k} className={`is-${v.tone}`}><v.icon size={14} aria-hidden="true" />{i.focus || i.question}</li>;
              })}
            </ul>
          </div>
        ) : busy === 'next' || !question ? (
          <div className="ai-thinking"><span className="ai-dots" aria-hidden="true"><i /><i /><i /></span>Montando a pergunta...</div>
        ) : (
          <>
            <span className="ai-review__eyebrow">Pergunta {items.length + (grade ? 0 : 1)}</span>
            <h3 className="ai-review__q">{question.question}</h3>
            {!grade ? (
              <textarea className="ss-input ai-review__answer" rows={4} value={answer} onChange={(e) => setAnswer(e.target.value)}
                        placeholder="Responda com as suas palavras..." disabled={busy === 'grade'}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }} autoFocus />
            ) : (
              <>
                <p className="ai-review__mine">{answer}</p>
                <GradeCard g={grade} onSource={onSource} />
              </>
            )}
          </>
        )}
      </div>

      <div className="ai-review__foot">
        {done ? (
          <Button block icon={RotateCcw} onClick={restart}>Nova rodada</Button>
        ) : grade ? (
          <>
            <button type="button" className="ai-linkbtn" onClick={saveCard} disabled={saved}>
              <Layers size={14} aria-hidden="true" />{saved ? 'Guardado nos flashcards' : 'Guardar como flashcard'}
            </button>
            <Button iconRight={ArrowRight} onClick={next} disabled={!!busy}>Próxima pergunta</Button>
          </>
        ) : (
          <Button block icon={Send} onClick={submit} disabled={!answer.trim() || !!busy || !question}>
            {busy === 'grade' ? 'Corrigindo...' : 'Responder'}
          </Button>
        )}
      </div>
    </div>
  );
}

function PrevResult({ item, n }) {
  const v = VERDICT[item.verdict] || VERDICT.ERRADA;
  return (
    <div className={`ai-review__prev is-${v.tone}`}>
      <v.icon size={14} aria-hidden="true" />
      <span>Pergunta {n} · {item.focus || item.question}</span>
      <b>{v.label}{item.xp ? ` · +${item.xp} XP` : ''}</b>
    </div>
  );
}

function GradeCard({ g, onSource }) {
  const v = VERDICT[g.verdict] || VERDICT.ERRADA;
  return (
    <div className={`ai-grade is-${v.tone}`}>
      <div className="ai-grade__head"><v.icon size={14} aria-hidden="true" /><span>{v.label}</span>{g.xp > 0 && <b>+{g.xp} XP</b>}</div>
      <div className="ai-grade__body">
        {g.right && <div className="ai-grade__line"><strong className="is-ok">Certo:</strong> <Markdown className="ai-md ai-md--inline">{g.right}</Markdown></div>}
        {g.missing && <div className="ai-grade__line"><strong className={g.verdict === 'ERRADA' ? 'is-bad' : g.verdict === 'CERTA' ? 'is-ok' : 'is-warn'}>{g.verdict === 'ERRADA' ? 'Na verdade:' : g.verdict === 'CERTA' ? 'Para completar:' : 'Faltou:'}</strong> <Markdown className="ai-md ai-md--inline">{g.missing}</Markdown></div>}
        {g.source && (
          <button type="button" className="ai-source" onClick={() => onSource?.(g.source)}><BookOpen size={12} aria-hidden="true" />{g.source}</button>
        )}
      </div>
    </div>
  );
}
